// Assertion shapes in the Console: which definitions to ask GET /v1/shapes
// for, and a manifest shape's fields, read from the Answer that carries it.
// See Design > Overview, "How answers render", and Design > Answer shapes,
// "Manifest shapes".

import { Option, Schema } from "effect";
import type { AnswerResponse } from "./answer.ts";
import type { ManifestShape, ShapeDefinition, ShapeField, ShapedAnswer } from "./types.ts";

/** The shapes of an answer's assertions and local Things, whose definitions describe their fields. */
export const nodeShapesOf = (a: ShapedAnswer): string[] =>
  [...new Set(Object.values(a.nodes).flatMap((n) => (n.kind === "collection" ? [] : [n.shape])))];

/**
 * The shapes of an answer to ask GET /v1/shapes for: its pinned
 * refs. A manifest shape travels with its answer, and /v1/shapes doesn't
 * serve it. See Design > Answer shapes, "Manifest shapes".
 */
export const shapesToFetchOf = (a: AnswerResponse): string[] =>
  nodeShapesOf(a.answer).filter((s) => !Object.hasOwn(a.shapes ?? {}, s));

// A typed field, as WarmHub's shape format writes one, for what the view
// reads of it: its description and its enum, whose members may be numbers.
const TypedField = Schema.Struct({ type: Schema.String, description: Schema.optionalKey(Schema.String), enum: Schema.optionalKey(Schema.Array(Schema.Json)) });
const typedFieldOf = Schema.decodeUnknownOption(TypedField);

/**
 * A manifest shape's fields, read for what the view shows: each field's
 * description and enum. WarmHub's shape format writes a field as a type name,
 * such as "number?", or as a typed field with its description and
 * constraints; a "?" after the name marks it optional.
 *
 * Display only, and not the Tool SDK's reader (manifestShapeDefOf in
 * src/tool-sdk/defs.ts), which the Console doesn't import: it reads only
 * descriptions and enums, keeps a type as written, "number?" included, and
 * reads a nested object as type "object" without its fields. The view shows
 * neither the type nor nested fields, so those differences don't show; a view
 * that does should read the SDK's normalized definition instead.
 */
export function manifestShapeFields(shape: ManifestShape): Record<string, ShapeField> {
  return Object.fromEntries(Object.entries(shape.fields).map(([name, spec]): [string, ShapeField] => {
    const key = name.endsWith("?") ? name.slice(0, -1) : name;
    if (typeof spec === "string") return [key, { type: spec }];
    const typed = Option.getOrUndefined(typedFieldOf(spec));
    if (!typed) return [key, { type: Array.isArray(spec) ? "array" : "object" }];
    return [key, {
      type: typed.type,
      ...(typed.description !== undefined ? { description: typed.description } : {}),
      ...(typed.enum ? { enum: typed.enum.map(String) } : {}),
    }];
  }));
}

/** Shape definitions by pinned ref. Null marks a ref that's been asked for: in flight, missing, or refused. */
export type ShapeCache = Record<string, ShapeDefinition | null>;

/**
 * The GET /v1/shapes requests to make for `refs`: each ref not yet asked for,
 * at most `max` per request. See Design > Exchange, "Fetch shape definitions".
 */
export function shapeBatches(refs: string[], have: ShapeCache, max: number): string[][] {
  const want = [...new Set(refs)].filter((r) => !(r in have));
  const batches: string[][] = [];
  for (let i = 0; i < want.length; i += max) batches.push(want.slice(i, i + max));
  return batches;
}
